/**
 * Match story pageImages[] slots the same way as ImageStudio (`matchPageImage`),
 * but with robust pageNumber coercion (avoids NaN comparisons that drop slots).
 */

export function coerceStoryPageNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    const n = Math.floor(value);
    return n >= 1 ? n : null;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const n = parseInt(value.trim(), 10);
    return Number.isFinite(n) && n >= 1 ? n : null;
  }
  return null;
}

export function normalizeIsSidePicture(v: unknown): boolean {
  return v === true || v === "true" || v === 1 || v === "1";
}

/** Primary match by pageNumber + slot; fallback when flags are inconsistent but exactly two rows share the page. */
export function findStoryPageSlotImage<T extends { pageNumber?: unknown; isSidePicture?: unknown }>(
  images: T[] | undefined,
  pageNum: number,
  wantSide: boolean
): T | undefined {
  if (!images?.length) return undefined;

  const direct = images.find((img) => {
    const pn = coerceStoryPageNumber(img.pageNumber);
    return pn === pageNum && normalizeIsSidePicture(img.isSidePicture) === wantSide;
  });
  if (direct) return direct;

  const onPage = images.filter((img) => coerceStoryPageNumber(img.pageNumber) === pageNum);
  if (onPage.length !== 2) return undefined;

  const [a, b] = onPage;
  const aSide = normalizeIsSidePicture(a.isSidePicture);
  const bSide = normalizeIsSidePicture(b.isSidePicture);
  /* One slot wrongly tagged but the other is correct */
  if (aSide !== bSide) {
    if (wantSide) return aSide ? a : b;
    return aSide ? b : a;
  }

  const sides = onPage.filter((img) => normalizeIsSidePicture(img.isSidePicture));
  const mains = onPage.filter((img) => !normalizeIsSidePicture(img.isSidePicture));
  if (wantSide && sides.length === 1) return sides[0];
  if (!wantSide && mains.length === 1) return mains[0];
  return undefined;
}
